/**
 * Admin-side SCORM / xAPI zip uploader for a single lesson.
 *
 * Posts a multipart/form-data request with field `file` to
 *   POST /v1/admin/training/modules/:moduleId/lessons/:lessonId/scorm-packages
 *
 * The api-client wrapper only does JSON, so we hit fetch() directly here
 * but reuse the same base-path + bearer-token resolution so this works
 * under the prefixed (`/xsc`) deploy.
 */
import { useRef, useState } from 'react';
import { Button } from '../ui/button.js';
import { getToken } from '../../lib/auth-store.js';

interface UploadResponse {
  package_id: string;
  manifest_version: 'scorm12' | 'scorm2004' | 'xapi';
  launch_path: string;
  title: string | null;
}

const PATH_PREFIX = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? '';
const PATH_PREFIX_NORMALIZED = PATH_PREFIX.replace(/\/$/, '');
const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? PATH_PREFIX_NORMALIZED;

interface Props {
  moduleId: string;
  lessonId: string;
  /** Called after the server has persisted the package and patched the
   *  lesson's content blocks. Callers should refresh the lesson from the
   *  server in response rather than mutating local content state. */
  onImported: () => void;
  /** When true, the copy reflects that uploading replaces the lesson's
   *  existing package (the server retires priors on import). */
  replace?: boolean;
}

export function ScormUpload({ moduleId, lessonId, onImported, replace }: Props): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const upload = async (file: File) => {
    setBusy(true);
    setErr(null);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      const headers: Record<string, string> = {};
      const token = getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(
        `${BASE_URL}/v1/admin/training/modules/${encodeURIComponent(moduleId)}/lessons/${encodeURIComponent(lessonId)}/scorm-packages`,
        { method: 'POST', headers, body: form, credentials: 'include' },
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Upload failed (${res.status})`);
      }
      await res.json().catch(() => null as UploadResponse | null);
      onImported();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="mt-3 p-3 rounded border border-dashed border-border bg-surface-alt/40 space-y-2">
      <div className="text-sm font-medium">
        {replace ? 'Replace SCORM / xAPI package' : 'SCORM / xAPI package'}
      </div>
      <p className="text-xs text-ink-muted">
        {replace
          ? 'Upload a new SCORM 1.2 / 2004 or Tin Can (xAPI) zip to replace the current package. The prior package and its storage are removed automatically.'
          : 'Upload a SCORM 1.2 / 2004 or Tin Can (xAPI) zip. The package will be extracted into private object storage and added as a block at the end of the lesson — remember to '}
        {!replace && <em>Save lesson</em>}
        {!replace && ' afterwards.'}
      </p>
      <input
        ref={inputRef}
        type="file"
        accept=".zip,application/zip,application/x-zip-compressed"
        disabled={busy}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void upload(f);
        }}
        className="block text-sm"
      />
      {busy && (
        <p className="text-xs text-ink-muted">
          Uploading and extracting… (large packages may take a minute)
        </p>
      )}
      {err && <p className="text-xs text-danger">{err}</p>}
      <div>
        <Button
          type="button"
          variant="secondary"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {replace ? 'Replace with zip…' : 'Choose zip…'}
        </Button>
      </div>
    </div>
  );
}
